import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navbar, Footer } from "@/components/Layout";
import { api, API } from "@/lib/api";
import { FileTextIcon, DownloadSimpleIcon, ShieldCheckIcon } from "@phosphor-icons/react";

export default function Agreement() {
  const [tpl, setTpl] = useState(null);

  useEffect(() => {
    api.get("/agreement-template").then((r) => setTpl(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />

      <section className="bg-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-500 mb-3 flex items-center gap-2">
            <FileTextIcon size={16} weight="fill" /> Sample Document
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            Service Agreement &<br />Liability Waiver
          </h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
            This is the standard contract MCC Service Corp uses for commercial and residential work. Every customer
            reviews and e-signs a copy of this agreement, pre-filled with their job details, before work begins.
          </p>
          <a
            href={`${API}/agreement/sample.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-sample-agreement"
            className="mt-8 inline-flex items-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-6 py-3 transition-colors active:scale-95"
          >
            <DownloadSimpleIcon size={18} weight="bold" /> Download PDF
          </a>
        </div>
      </section>

      <div className="flex-1 max-w-4xl mx-auto w-full px-6 md:px-12 py-14">
        {!tpl ? (
          <p className="text-slate-400">Loading agreement…</p>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="agreement-content"
            className="bg-white border border-slate-200 rounded-sm p-8 md:p-12 shadow-crisp"
          >
            <h2 className="font-heading text-2xl font-bold text-slate-900">{tpl.title}</h2>
            <p className="mt-2 text-sm text-slate-500">Contractor: {tpl.contractor}</p>
            {tpl.contractor_details && (
              <p className="mt-1 text-xs text-slate-400">
                {[tpl.contractor_details.address, tpl.contractor_details.phone, tpl.contractor_details.email, tpl.contractor_details.license && `Lic# ${tpl.contractor_details.license}`].filter(Boolean).join("  ·  ")}
              </p>
            )}
            <p className="mt-6 text-slate-700 leading-relaxed">{tpl.intro}</p>

            <div className="mt-8 space-y-7">
              {tpl.clauses.map((c) => (
                <div key={c.heading} data-testid="agreement-clause">
                  <h3 className="font-heading text-lg font-semibold text-slate-900">{c.heading}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <div className="h-10 border-b border-slate-400" />
                <p className="mt-2 text-sm text-slate-500">{tpl.contractor} (Contractor)</p>
              </div>
              <div>
                <div className="h-10 border-b border-slate-400" />
                <p className="mt-2 text-sm text-slate-500">Client Signature &amp; Date</p>
              </div>
            </div>

            <div className="mt-10 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
              <ShieldCheckIcon size={18} className="text-gold-600 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-500 leading-relaxed">{tpl.disclaimer}</p>
            </div>
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
}
